import { useId, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import ProgressBar from 'react-bootstrap/ProgressBar';
import {
  FaArrowRotateRight,
  FaBroom,
  FaCompress,
  FaExpand,
  FaPause,
  FaPlay,
  FaSignal,
  FaStop,
  FaVolumeHigh,
  FaVolumeXmark,
} from 'react-icons/fa6';
import '../../assets/videoPlayer.css';
import VideoControlButton from '../VideoControlButton/VideoControlButton';

export default function VideoPlayerControls({
  bufferedProgress,
  bufferedSecondsAhead,
  controlsLabel = 'Video playback controls',
  controlsDisabled,
  fitLabel,
  fitDescription,
  loading,
  mediaId,
  muted,
  onCycleFitMode,
  onHandleVolumeSlider,
  onPause,
  onRestart,
  onResetAndRestart,
  onResume,
  onSeek,
  onStop,
  onToggleMute,
  paused,
  progress,
  volume,
  fullscreenLabel,
  isFullscreen,
  onToggleFullscreen,
  fullscreenAvailable = true,
}) {
  const [showBufferDetails, setShowBufferDetails] = useState(false);
  const controlsId = useId();
  const bufferStatusId = `${controlsId}-buffer-status`;
  const bufferDetailsId = `${controlsId}-buffer-details`;
  const safeBufferedProgress = Number.isFinite(bufferedProgress) ? bufferedProgress : 0;
  const safeProgress = Number.isFinite(progress) ? progress : 0;
  const safeVolume = Number.isFinite(volume) ? volume : 0;
  const displayedVolume = muted ? 0 : safeVolume;
  const playbackControls = [
    {
      icon: paused ? (
        <FaPlay size={18} aria-hidden="true" focusable="false" />
      ) : (
        <FaPause size={18} aria-hidden="true" focusable="false" />
      ),
      onClick: paused ? onResume : onPause,
      title: paused ? 'Resume' : 'Pause',
      className: 'p-0 vms-video-control-button-primary',
    },
    {
      icon: <FaStop size={16} aria-hidden="true" focusable="false" />,
      onClick: onStop,
      title: 'Stop',
    },
    {
      icon: <FaArrowRotateRight size={16} aria-hidden="true" focusable="false" />,
      onClick: onRestart,
      title: 'Restart stream',
    },
    {
      icon: <FaBroom size={16} aria-hidden="true" focusable="false" />,
      onClick: onResetAndRestart,
      title: 'Clear HLS cache and restart',
    },
  ];

  const bufferLabel = loading
    ? 'Reconnecting'
    : bufferedSecondsAhead >= 3
      ? 'Stable'
      : bufferedSecondsAhead >= 1.5
        ? 'Catching Up'
        : 'Low Buffer';
  const bufferTier = loading
    ? 0
    : bufferedSecondsAhead >= 3
      ? 3
      : bufferedSecondsAhead >= 1.5
        ? 2
        : 1;
  const signalColor = bufferTier >= 3 ? '#22c55e' : bufferTier === 2 ? '#facc15' : '#ef4444';
  const progressPercent = Math.round(safeProgress * 100);
  const progressValueText = `Playback position: ${progressPercent}% of available footage`;
  const volumePercent = Math.round(displayedVolume * 100);
  const volumeValueText = muted ? 'Muted' : `${volumePercent}%`;
  const bufferStatusText = `Stream buffer status: ${bufferLabel}`;

  const handleSeekChange = (event) => {
    const seekProgress = Number(event.currentTarget.value);
    if (!Number.isFinite(seekProgress)) return;

    onSeek(seekProgress);
  };

  return (
    <div
      className="vms-video-controls d-flex flex-column"
      data-buffer-tier={bufferTier}
      role="group"
      aria-label={controlsLabel}
    >
      <div className="vms-video-progress-rail" style={{ '--progress-scale': String(safeProgress) }}>
        <span className="vms-video-progress-rail-fill" aria-hidden="true" />
        <span className="vms-video-progress-thumb" aria-hidden="true" />
        <input
          className="vms-video-progress-scrubber"
          type="range"
          min="0"
          max="1"
          step="0.001"
          value={safeProgress}
          onChange={handleSeekChange}
          aria-controls={mediaId}
          aria-label="Seek loaded video position"
          aria-valuetext={progressValueText}
          title={progressValueText}
          disabled={controlsDisabled}
        />
      </div>

      <div className="vms-video-control-strip d-flex flex-wrap align-items-center justify-content-between gap-2">
        <div className="vms-video-primary-controls d-flex align-items-center gap-2">
          {playbackControls.map((control) => (
            <VideoControlButton
              key={control.title}
              title={control.title}
              ariaControls={mediaId}
              onClick={control.onClick}
              disabled={controlsDisabled}
              className={control.className}
            >
              {control.icon}
            </VideoControlButton>
          ))}

          <div className="vms-video-volume-control d-flex align-items-center">
            <VideoControlButton
              className="p-0 vms-video-volume-button"
              title={muted ? 'Unmute' : 'Mute'}
              ariaControls={mediaId}
              ariaLabel="Mute audio"
              ariaPressed={muted}
              onClick={onToggleMute}
              disabled={controlsDisabled}
            >
              {muted ? (
                <FaVolumeXmark size={17} aria-hidden="true" focusable="false" />
              ) : (
                <FaVolumeHigh size={17} aria-hidden="true" focusable="false" />
              )}
            </VideoControlButton>

            <div className="vms-video-volume-slider-shell d-flex align-items-center">
              <Form.Range
                className="vms-video-volume-slider cursor-pointer align-self-center mb-0"
                min="0"
                max="1"
                step="0.05"
                value={displayedVolume}
                onChange={onHandleVolumeSlider}
                title={`Volume: ${volumePercent}%`}
                aria-controls={mediaId}
                aria-label="Volume"
                aria-valuetext={volumeValueText}
                disabled={controlsDisabled}
              />
            </div>
          </div>
        </div>

        <div className="vms-video-secondary-controls d-flex flex-wrap align-items-center justify-content-end gap-2">
          <div
            className="vms-video-buffer-status d-flex flex-shrink-0 align-items-center gap-2 text-white small fw-semibold"
            data-expanded={showBufferDetails}
            data-buffer-tier={bufferTier}
          >
            <span id={bufferStatusId} className="visually-hidden" role="status" aria-live="polite">
              {bufferStatusText}
            </span>
            <Button
              variant="link"
              className="vms-video-buffer-toggle d-inline-flex align-items-center justify-content-center rounded-circle border-0 p-0 text-decoration-none"
              onClick={() => setShowBufferDetails((prev) => !prev)}
              aria-label={`${showBufferDetails ? 'Hide' : 'Show'} buffer status details`}
              aria-expanded={showBufferDetails}
              aria-controls={bufferDetailsId}
              aria-describedby={bufferStatusId}
            >
              <FaSignal size={14} color={signalColor} aria-hidden="true" focusable="false" />
            </Button>
            <span className="vms-video-buffer-label d-none d-sm-inline" aria-hidden="true">
              {bufferLabel}
            </span>
          </div>

          <VideoControlButton
            className="vms-video-fit-button"
            title={fitDescription}
            ariaControls={mediaId}
            ariaLabel={`Change video fit mode. Current mode: ${fitDescription}`}
            onClick={onCycleFitMode}
            disabled={controlsDisabled}
          >
            {fitLabel}
          </VideoControlButton>

          <VideoControlButton
            className="p-0 vms-video-fullscreen-button"
            title={fullscreenLabel}
            ariaControls={mediaId}
            ariaPressed={isFullscreen}
            onClick={onToggleFullscreen}
            disabled={controlsDisabled || !fullscreenAvailable}
          >
            {isFullscreen ? (
              <FaCompress size={15} aria-hidden="true" focusable="false" />
            ) : (
              <FaExpand size={15} aria-hidden="true" focusable="false" />
            )}
          </VideoControlButton>
        </div>
      </div>
      <div
        id={bufferDetailsId}
        className={`vms-video-buffer-details align-items-center gap-2 mt-2 px-2 ${showBufferDetails ? 'd-flex' : 'd-none'}`}
        hidden={!showBufferDetails}
      >
        <span aria-hidden="true">BUFFER:</span>
        <ProgressBar
          className="vms-video-buffer-progress flex-grow-1"
          now={safeBufferedProgress * 100}
          variant={
            bufferedSecondsAhead >= 3
              ? 'success'
              : bufferedSecondsAhead >= 1.5
                ? 'warning'
                : 'danger'
          }
          aria-label={`Buffer level: ${bufferLabel}`}
        />
      </div>
    </div>
  );
}
